import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  BookOpenText,
  Bot,
  Check,
  ChevronDown,
  Copy,
  Lightbulb,
  RotateCcw,
} from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { useAdminView } from "../../../shared/hooks/useAdminView";
import { useHasAdminRole } from "../../../shared/hooks/useHasRole";
import { ChatShort } from "../../../shared/models/Chat";
import ChatInfo from "./ChatInfo";
import { ChatMessage } from "../models/ChatMessage";
import { PrimaryChatUserInputData } from "../models/message-data/PrimaryChatUserInputData";
import UserInputMessage from "./messages/UserInputMessage";
import { ModelOutputMessageData } from "../models/message-data/ModelOutputMessageData";
import { ExecutionPlanNew } from "./ExecutionPlanNew";
import CenterLoader from "../../../shared/components/loader/CenterLoader";
import { useClipboard, useMediaQuery } from "@mantine/hooks";
import { isLargeScreenMediaQuery } from "../../../shared/utils/ResponsiveStyles";
import Timer from "../../../shared/components/timer/Timer";
import { MessageProcessor } from "../models/MessageDataProcessor";
import { Citation, CitationIndex } from "../../sources/models/Citation";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ContentWithCitations } from "@/features/sources/models/ContentWithCitations";
import { errorNotification } from "@/shared/utils/Notifications";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import ScrollableText from "@/shared/components/scrollable-text/ScrollableText";
import { useRetryChatMessage } from "../api/useRetryChat";
import { MessageRole } from "../enums/MessageRole";

interface MessagesListProps {
  chat: ChatShort;
  messages: ChatMessage[];
  autoScroll?: boolean;
  onCitationClick?: (citation: CitationIndex) => void;
  onSourcesClick?: (citations: Citation[]) => void;
}

export default function MessageList({
  messages,
  chat,
  autoScroll = true,
  onCitationClick,
  onSourcesClick,
}: MessagesListProps) {
  const isAdmin = useHasAdminRole();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isAdminView] = useAdminView();
  const clipboard = useClipboard({ timeout: 2000 });
  const { retryChatMessage, isRetrying } = useRetryChatMessage(chat.id);

  const scrollToBottom = () => {
    if (!scrollRef.current || !autoScroll) return;

    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "instant",
    });
    setIsAtBottom(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const isUserAtBottom =
        Math.abs(scrollHeight - scrollTop - clientHeight) < 10;
      setIsAtBottom(isUserAtBottom);
    };

    const scrollElement = scrollRef.current;
    scrollElement?.addEventListener("scroll", handleScroll);

    // Only scroll to bottom on mount if autoScroll is true
    if (autoScroll) {
      setTimeout(scrollToBottom, 5);
    }

    return () => scrollElement?.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScroll]);

  const isLargeScreen = useMediaQuery(isLargeScreenMediaQuery);

  const handleSourcesClick = (message: ChatMessage) => {
    if (message.role !== MessageRole.Assistant) return;
    const modelOutputData = message.data as ModelOutputMessageData;
    const contentWithCitations = new ContentWithCitations(
      modelOutputData.content,
    );
    if (!contentWithCitations.citations.length) {
      errorNotification("No sources were used");
      return;
    }

    onSourcesClick?.(contentWithCitations.citations);
  };

  return (
    <div className="relative flex-grow flex flex-col overflow-hidden">
      <div className="flex-grow overflow-hidden" ref={scrollRef}>
        <div className="space-y-6 px-2 pt-4">
          {isAdmin && isAdminView && chat && <ChatInfo chat={chat} />}
          <div className="space-y-6">
            {messages.map((message, i) => (
              <div key={message.id} className="px-2">
                {message.errorMessage || message.isCancelled ? (
                  <div className="space-y-2">
                    <Card className="flex-1 p-4">
                      <div className="flex items-start gap-4">
                        <Bot className="h-5 w-5 flex-shrink-0 text-foreground" />
                        <div className="flex-1 min-w-0">
                          {isAdmin && isAdminView && (
                            <div className="flex justify-between items-center mb-2">
                              <p className="text-sm text-muted-foreground">
                                {message.role}
                              </p>
                              <Timer
                                startedAt={message.startedAt}
                                finishedAt={message.finishedAt}
                                variant="neutral"
                              />
                            </div>
                          )}
                          {message.errorMessage && (
                            <div className="text-sm text-destructive">
                              {message.errorMessage}
                            </div>
                          )}
                          {message.isCancelled && (
                            <div className="text-sm text-destructive">
                              Chat was stopped by the user.
                            </div>
                          )}
                          {i === messages.length - 1 && (
                            <div className="flex justify-center">
                              <Button
                                onClick={() => retryChatMessage(message.id)}
                                disabled={isRetrying}
                              >
                                <RotateCcw />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  </div>
                ) : !message.data ? (
                  <CenterLoader size="sm" />
                ) : (
                  (() => {
                    const data = message.data;
                    const functionCalls = (data as ModelOutputMessageData)
                      .functionCalls;

                    if (message.role === MessageRole.User) {
                      return (
                        <UserInputMessage
                          key={message.id}
                          data={data as PrimaryChatUserInputData}
                          chatId={chat.id}
                        />
                      );
                    }

                    if (
                      message.role === MessageRole.Assistant &&
                      !functionCalls
                    ) {
                      return (
                        <div className="space-y-2">
                          <Card className="flex-1 p-4">
                            <div className="flex items-start gap-4">
                              <Bot className="h-5 w-5 flex-shrink-0 text-foreground" />
                              <div className="flex-1 min-w-0 overflow-x-auto">
                                {isAdmin && isAdminView && (
                                  <div className="flex justify-between items-center mb-2">
                                    <p className="text-sm text-muted-foreground">
                                      {message.role}
                                    </p>
                                    <Timer
                                      startedAt={message.startedAt}
                                      finishedAt={message.finishedAt}
                                      variant="neutral"
                                    />
                                  </div>
                                )}
                                {MessageProcessor.GetComponent(
                                  message.role,
                                  data,
                                  message.inProgress,
                                  onCitationClick,
                                )}
                                {message.errorMessage && (
                                  <div className="mt-2 text-sm text-destructive">
                                    {message.errorMessage}
                                  </div>
                                )}
                              </div>
                            </div>
                            <Tooltip delayDuration={100}>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-gray-400 hover:text-gray-600 ml-6"
                                  onClick={() => {
                                    if (message.role !== MessageRole.Assistant)
                                      return;
                                    const modelOutputData =
                                      message.data as ModelOutputMessageData;
                                    const content = modelOutputData.content;
                                    clipboard.copy(content);
                                  }}
                                >
                                  {!clipboard.copied ? <Copy /> : <Check />}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Copy
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip delayDuration={100}>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-gray-400 hover:text-gray-600"
                                  onClick={() => handleSourcesClick(message)}
                                >
                                  <BookOpenText />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Sources
                              </TooltipContent>
                            </Tooltip>
                            {message.role === MessageRole.Assistant &&
                              (data as ModelOutputMessageData).thinking && (
                                <Popover modal={false}>
                                  <Tooltip delayDuration={100}>
                                    <PopoverTrigger asChild>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="text-gray-400 hover:text-gray-600"
                                        >
                                          <Lightbulb />
                                        </Button>
                                      </TooltipTrigger>
                                    </PopoverTrigger>
                                    <TooltipContent side="bottom">
                                      Reasoning
                                    </TooltipContent>
                                  </Tooltip>
                                  <PopoverContent className="w-[500px] h-full rounded-md">
                                    <ScrollableText
                                      content={
                                        (data as ModelOutputMessageData)
                                          .thinking
                                      }
                                    />
                                  </PopoverContent>
                                </Popover>
                              )}
                          </Card>
                        </div>
                      );
                    }

                    const isPreviousUserMessage =
                      messages[i - 1]?.role === MessageRole.User;
                    const isPreviousAssistantMessageWithNoFunctionCalls =
                      messages[i - 1]?.role === MessageRole.Assistant &&
                      !(messages[i - 1].data as ModelOutputMessageData)
                        ?.functionCalls;
                    const isPreviosMessageCancelled =
                      messages[i - 1]?.isCancelled;
                    if (
                      !isPreviousUserMessage &&
                      !isPreviousAssistantMessageWithNoFunctionCalls &&
                      !isPreviosMessageCancelled
                    )
                      return null;

                    const executionPlanMessages = [] as ChatMessage[];
                    let executionPlanFinished = false;
                    // Find all function calls before next model output without function call
                    const nextModelOutputMessageIndex = messages
                      .slice(i)
                      .findIndex((m) => {
                        return (
                          m.role === MessageRole.Assistant &&
                          !(m.data as ModelOutputMessageData)?.functionCalls
                        );
                      });

                    if (nextModelOutputMessageIndex === -1) {
                      executionPlanMessages.push(...messages.slice(i));
                    } else {
                      executionPlanMessages.push(
                        ...messages.slice(i, i + nextModelOutputMessageIndex),
                      );

                      // Only set executionPlanFinished if the next message is finished
                      // and has model output without function calls
                      const nextMessage =
                        messages[i + nextModelOutputMessageIndex];
                      const nextMessageData =
                        nextMessage.data as ModelOutputMessageData;
                      executionPlanFinished =
                        nextMessage.finishedAt !== undefined &&
                        (nextMessageData?.functionCalls === undefined ||
                          nextMessageData?.functionCalls.length === 0);
                    }

                    if (executionPlanMessages.length !== 0)
                      return (
                        <div className="w-full">
                          <ExecutionPlanNew
                            key={message.id}
                            messages={executionPlanMessages}
                            isFinished={executionPlanFinished}
                          />
                        </div>
                      );
                  })()
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {!isAtBottom && (
        <Button
          variant="outline"
          size={isLargeScreen ? "default" : "sm"}
          className="absolute right-1/2 translate-x-1/2 bottom-4 shadow-md"
          onClick={scrollToBottom}
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
